/**
 * insuranceArithmetic.ts — what one year's premium figures make true, derived.
 *
 * THE SIDE SHEET'S RULES, RE-HOMED, BECAUSE IT WAS RETIRED AND THE RULES WERE NOT. Which of
 * two instruments a year is published on [D-B45] turns on the loan's own rows — deposits covering
 * the premium AND no instalment row — and `agreement_create` derives it at the moment of
 * generation, so a screen that is about to WRITE those rows has to read the same two limbs off the
 * draft it is about to write. The Add Insurance side sheet said that first, in its own vocabulary;
 * this says it in the dossier's, and that copy was retired with the sheet in c0fee0e.
 *
 * WHAT CHANGED IN THE RE-HOME IS THE DEPOSIT, AND IT CHANGED BECAUSE THE DOSSIER CAN SEE MORE. The
 * sheet bound exactly one deposit row and judged coverage on its amount alone; a junction may carry
 * more (production has one — loan 294, 3000 + 977 against a premium of 3977), and the dossier binds
 * the LOWEST-id link while the others stay on the rail. So coverage is judged on the SUM: the other
 * deposits, which this screen does not edit, plus the one it does. Judging it on the edited row
 * alone would print an Advance over a premium the driver has in fact settled.
 *
 * AND THE MONEY IS NET OF WHAT THE DRIVER PAID [D-B46]. The ledger has always charged interest on
 * `amount − deposits`; `insuranceMoney` below is that arithmetic, spelled the way
 * `insights/advanceDebt.ts` spells it so the dossier and the Insurance tab cannot state two
 * different debts about one loan.
 *
 * PURE AND `.ts`, so vitest can reach it — `vitest.config.ts` includes `src/**\/*.test.ts` and runs
 * node-env, so nothing inside a `.tsx` canvas has ever been under test. The structural draft type
 * means `InsuranceFormValues` satisfies it without this module importing the form.
 *
 * DISPLAY-ONLY, AND NAMED AS SUCH against `.agents/rules/financial-parity.md`: none of the five
 * protected numbers is recomputed here and nothing is written anywhere.
 */

import {
    FULL_DEPOSIT_TOLERANCE,
    instrumentFor,
    type InsuranceInstrument,
} from '@/lib/agreements/instrument';

import type { WeeklyInstalmentRow } from '../advance/advanceArithmetic';
import { parseAdvanceNumber } from '../advance/advanceForm';

/**
 * THE FORWARD WEEKLY SUM AND THE SCHEDULE'S TERM ARE THE ADVANCE'S, VERBATIM. Both are asked of an
 * insurance premium advance in exactly the words they are asked of an operational one — the weekly
 * recovery is added to the contract instalment the same way, and the term is the total repayable
 * over the weekly figure with the same half-pesewa guard [D-T1]. They are re-exported rather than
 * re-implemented so the two dossiers cannot drift into two answers, and `advanceArithmetic.ts`
 * carries the whole argument for each.
 */
export { derivedTerm, forwardWeeklyTotal } from '../advance/advanceArithmetic';
export type {
    DerivedTerm,
    ForwardWeeklyTotal,
    WeeklyInstalmentRow,
} from '../advance/advanceArithmetic';

/**
 * The forward sum's insurance term, with THIS policy's own loan taken out of it.
 *
 * THE EXCLUSION IS `forwardWeeklyTotal`'s CONTRACT, and this is the insurance dossier's half of it.
 * That function adds the figure being typed to the Σ of every standing recovery, so the recovery
 * being typed over must not already be in the Σ — the advance dossier drops its own `loans` row the
 * same way (`useAdvanceState.ts:505-517`). Leaving it in counts the rate on file beside the one in
 * the box on every edit, and states a weekly total the driver will never be billed.
 *
 * IT IS A DERIVATION OVER A SHARED CACHE ENTRY AND NEVER A NARROWED READ. `advanceForwardKey` is
 * one entry both dossiers consume, and the advance's own panel needs every insurance loan on the
 * contract — so the payload stays whole and each page filters what it draws.
 *
 * NULL IS "NOTHING TO EXCLUDE", which is the create flow and the 54 junctions carrying no loan:
 * there is no row of this policy's in the list, so the list is the answer.
 */
export function otherInsuranceInstalments(
    rows: WeeklyInstalmentRow[],
    loanId: number | null | undefined,
): WeeklyInstalmentRow[] {
    if (loanId === null || loanId === undefined) return rows;
    return rows.filter((row) => row.loanId !== loanId);
}

/**
 * The form fields that decide the shape. `InsuranceFormValues` satisfies this structurally.
 *
 * STRINGS, BECAUSE THESE ARE THE BOXES AND NOT THE ROWS. Every predicate below is asked of what the
 * admin has typed, before anything is written — that is the whole point of asking.
 */
export interface InsuranceLoanDraft {
    /** The typed premium — `insurance_loans.amount` once parsed. */
    premiumAmount: string;
    /** `insurance_loan_deposits.transaction_id` as the picker holds it: '' when nothing is linked. */
    depositTransactionId: string;
    /** The linked payment's own amount, RAW — the ledger's sign stays on, as the server reads it. */
    depositAmount: number | null;
    /** `insurance_loan_instalments.amount`, as typed. */
    weeklyAmount: string;
    /** `insurance_loan_instalments.starts_at`, as typed. */
    firstStartsAt: string;
}

/**
 * THE FIGURES ARE PARSED THE WAY THE WRITER PARSES THEM, and that is a deliberate change from the
 * sheet's `parseFloat`. `parseFloat('12abc')` is 12 while the commit's `parseAdvanceNumber` reads
 * the same box as nothing at all — so on that one shape the screen would have promised a Payment
 * Record over a premium the save then refused. One parser for the screen and the writer.
 */
function figure(value: string): number | null {
    return parseAdvanceNumber(value);
}

/**
 * Will this save write an `insurance_loan_instalments` row?
 *
 * THE SECOND LIMB OF THE INSTRUMENT, ASKED OF THE DRAFT. `agreement_create` publishes an Advance
 * wherever an instalment row exists, whatever the deposits say, so this is what decides the
 * instrument alongside the coverage below.
 *
 * A TYPED ZERO PASSES IT, AND THE REFUSAL FOR THAT LIVES ELSEWHERE. The dossier refuses a weekly of
 * nought in `validateDraft` — the side sheet wrote one — but this predicate's job is to say what the
 * rows would be, not whether the save is allowed to happen. A predicate that also enforced the
 * refusal would answer "no instalment" for a draft the admin can still see on screen.
 */
export function willWriteInstalment(draft: InsuranceLoanDraft): boolean {
    return figure(draft.weeklyAmount) !== null && draft.firstStartsAt.trim() !== '';
}

/**
 * What the driver will have paid towards this premium once this save lands.
 *
 * `depositAmount` OUTLIVES ITS TRANSACTION, SO THE ID IS THE GATE. The save writes an
 * `insurance_loan_deposits` row only where a transaction is linked — and removes the link where it
 * is not — so an amount with no id behind it is money that never reaches the database.
 *
 * `otherDeposits` IS THE Σ OF THE ROWS THIS SCREEN DOES NOT EDIT, and it defaults to nought because
 * that is nearly always the truth: one production loan carries a second deposit row. It is a
 * parameter rather than a lookup because the rows come off the policy read, and a predicate that
 * fetched them would be a second opinion about what is on the loan.
 */
export function draftDepositTotal(draft: InsuranceLoanDraft, otherDeposits = 0): number {
    const linked = draft.depositTransactionId.trim() !== '';
    return otherDeposits + (linked ? (draft.depositAmount ?? 0) : 0);
}

/**
 * Do these payments cover this premium? — the rule itself, over two figures.
 *
 * THE ONE AUTHOR OF THE ANSWER, AND IT TAKES FIGURES BECAUSE ITS THREE CALLERS HOLD DIFFERENT
 * THINGS. The canvas asks it of the form's strings, the writer of the parsed draft, and the
 * proof-read of a total it was handed — and all three have to agree exactly, because the screen says
 * "paid in full" off the first, the stored `interest_rate` is zeroed off the second [D-B45] and the
 * third prints the percentage the document will carry. Three spellings of one comparison is three
 * chances for a year to be stored with a rate its own Schedule contradicts.
 *
 * HALF A PESEWA OF TOLERANCE, THE SERVER'S OWN FIGURE, and it is imported rather than restated: a
 * premium settled to the last pesewa reads as 0.004 short of two `numeric` sums compared for
 * equality, and prints "The Borrower did not pay the whole of it" over a driver who did.
 *
 * A PREMIUM OF NOUGHT OR NONE IS NEVER COVERED. Nothing has been said about the year yet, and a
 * predicate answering yes there would zero a rate over a blank box.
 */
export function depositsCoverPremium(premium: number | null, depositTotal: number): boolean {
    if (premium === null || !(premium > 0)) return false;
    return depositTotal >= premium - FULL_DEPOSIT_TOLERANCE;
}

/**
 * Do the driver's payments cover the whole premium? — the copy on the advance canvas.
 *
 * THE INSTRUMENT'S FIRST LIMB ON ITS OWN, and it is asked separately on purpose: a payment that
 * covers the premium is a true and useful thing to tell an admin even where a weekly recovery
 * already on the loan is still going to keep the year on an Advance, which is exactly the case
 * where this and `insuranceInstrument` disagree.
 */
export function depositsCoverAmount(draft: InsuranceLoanDraft, otherDeposits = 0): boolean {
    return depositsCoverPremium(
        figure(draft.premiumAmount),
        draftDepositTotal(draft, otherDeposits),
    );
}

/**
 * The instrument `agreement_create` will pick for the loan this save is about to write.
 *
 * THROUGH `instrumentFor` AND NOT A FOURTH SPELLING OF THE RULE — the same two limbs, the same half
 * pesewa, the same fall-back-to-the-advance on every unknown. What this function does is translate
 * a dossier draft into the shape that predicate reads.
 */
export function insuranceInstrument(
    draft: InsuranceLoanDraft,
    otherDeposits = 0,
): InsuranceInstrument {
    return instrumentFor({
        amount: figure(draft.premiumAmount),
        depositTotal: draftDepositTotal(draft, otherDeposits),
        // A COUNT AND NOT A FLAG: the save writes at most one instalment row, so one is the count
        // the server's `NOT EXISTS` will find.
        instalmentCount: willWriteInstalment(draft) ? 1 : 0,
    });
}

/** What a caller has to tell this module about the loan behind the draft. */
export interface InsuranceEditContext {
    /**
     * IS THE WEEKLY RECOVERY A ROW IN THE DATABASE, or a plan somebody typed in this session?
     *
     * `existing.instalmentId !== null`, and it is the first thing the edit below asks. Clearing the
     * two weekly fields is not a tidy-up on a hydrated loan: the commit's else-arm DELETES the row,
     * which moves `weekly_rate` — one of the five parity-protected numbers — as a side effect of
     * linking a payment, with nothing on screen saying a live recovery is being removed.
     */
    instalmentSaved: boolean;
    /** The Σ of the deposit rows this screen does not edit. Nought unless the loan carries more. */
    otherDeposits?: number;
}

/**
 * Every edit the canvas makes, with a TYPED weekly dropped on the edit that makes the driver's
 * payment cover the premium [D-B45].
 *
 * THE FIGURES OUTLIVE THE REASON FOR THEM. An admin who types a weekly recovery and then links a
 * payment covering the whole premium has described two contradictory years: nothing was advanced,
 * and yet something is being recovered weekly. The save writes what the boxes hold, and
 * `agreement_create` then finds an instalment, fails its `NOT EXISTS` limb and publishes an Advance
 * over a premium nobody advanced. The values are dropped here instead, at the one door every edit
 * passes through.
 *
 * NEVER OVER A ROW THAT ALREADY EXISTS, WHICH IS THE FIRST THING THIS ASKS — see `instalmentSaved`.
 * A plan typed in this session is the canvas's to drop; a recovery already running against a live
 * contract is not, and removing it stays an explicit act with a line on screen about it.
 *
 * AND ON THE EDGE, NOT ON THE STATE. Even for a typed plan the clear fires only where THIS update
 * is what makes the payment cover the premium; a draft that already covered it is left as it is, so
 * no single edit reaches back and undoes a decision an earlier one made.
 *
 * GOING BACK RESTORES THE FIELDS EMPTY, NOT THE FIGURES. Unlinking the payment leaves the weekly
 * pair blank, which is the honest offer: the plan that was typed was for a premium the driver has
 * since paid, and re-typing it is a decision, not a restoration.
 */
export function applyInsuranceEdit<T extends InsuranceLoanDraft>(
    prev: T,
    updates: Partial<T>,
    { instalmentSaved, otherDeposits = 0 }: InsuranceEditContext,
): T {
    const next = { ...prev, ...updates };
    if (instalmentSaved) return next;
    if (!depositsCoverAmount(next, otherDeposits) || depositsCoverAmount(prev, otherDeposits)) {
        return next;
    }
    return { ...next, weeklyAmount: '', firstStartsAt: '' };
}

export interface InsuranceMoney {
    /** `insurance_loans.amount` — the PREMIUM for the year, never the amount advanced. */
    premium: number | null;
    /** What the driver paid towards it — the Σ of this loan's deposits. */
    deposit: number;
    /** The Amount advanced: `A − D` [D-B46]. */
    principal: number | null;
    /** Interest on the amount advanced, and on nothing else. */
    interest: number | null;
    /** The Total repayable — `principal + interest`, which is `(A − D)(1 + r/100)`. */
    total: number | null;
}

/**
 * The premium year's money, NET of what the driver paid [D-B46].
 *
 * THE OWNER'S RULING, AND THE LEDGER'S ARITHMETIC BOTH: *"when a driver pays part of the premium,
 * the interest is only calculated on the remaining amount advanced by Primelane."*
 * `get_contract_insights` and `calculate_overdue_interest` have charged on `amount − deposits`
 * throughout — the screens were what stated it otherwise.
 *
 * SPELLED THE WAY `advanceDebt.ts` SPELLS ITS ADVANCE BRANCH, character for character, and its test
 * pins the two together over that branch. The two surfaces describe the same loan on the same
 * contract, and a second grouping of the same product is not guaranteed to agree in double
 * precision.
 *
 * THE PAID-IN-FULL BRANCH IS NOT HERE, AND IS NOT MISSING. `advanceDebt` answers a Payment Record
 * year with `principal = amount` and `interest = 0` — the premium on both sides of the ratio it
 * feeds — which is the Record's convention and not this function's: on a Record nothing was
 * advanced, and the rows that state so are `insuranceSummary`'s `recordClauses`, which does its own
 * arithmetic and never calls this. So a caller wanting a Record's figures asks that; this answers
 * what Primelane put in.
 *
 * NOT CLAMPED, because the ledger's own reading is not: the interest inside
 * `get_contract_insights.total_value` is charged on this same unguarded difference, and a client
 * that clamped where the server does not would state a different debt from the tiles above it.
 *
 * A ZERO RATE IS A FIGURE AND NOT AN ABSENCE (`COALESCE(v_loan_rate, 0)`): a premium advanced
 * interest-free repays exactly what was advanced. A premium nobody has typed is nothing at all —
 * null, never nought — because a nought here would print "Total repayable GH₵0.00" over a year
 * whose figures have simply not been entered yet.
 */
export function insuranceMoney(
    premium: number | null,
    depositTotal: number | null,
    rate: number | null,
): InsuranceMoney {
    const deposit = depositTotal ?? 0;
    if (premium === null || premium === undefined) {
        return { premium: null, deposit, principal: null, interest: null, total: null };
    }
    const principal = premium - deposit;
    const interest = (principal * (rate ?? 0)) / 100;
    return { premium, deposit, principal, interest, total: principal + interest };
}

export interface PrintedMoney extends InsuranceMoney {
    /** The rate the save will STORE, which is not always the one in the box [D-B45]. */
    rate: number;
}

/**
 * The money grid AS THE DOCUMENT WILL PRINT IT — the one author of the two adjustments the
 * instrument makes to the draft, for the two surfaces that have to show the same figures.
 *
 * THE PROOF-READ AND THE CANVAS ARE THE SAME CLAIM MADE TWICE. The Advance canvas states what the
 * instrument will print while it is being typed and the confirm dialog states it one moment before
 * the policy exists; a second spelling of either adjustment is a screen that disagrees with the
 * dialog an admin confirms on, over figures a driver signs.
 *
 * THE RATE IS THE ONE THE SAVE WILL STORE [D-B45]. `loanValues` zeroes it wherever the driver's
 * payments cover the premium, whatever was typed — and an Advance is exactly where the two can
 * disagree: `instrumentFor` keeps a year on the Advance as soon as an instalment row will exist,
 * whatever the deposits say, so a covering deposit and a typed weekly pair coexist. Printing 43 per
 * cent over a save that stores 0 would be the surface whose whole job is showing what the document
 * will print, printing something else.
 *
 * AND THE PAID FIGURE IS CLAMPED THE WAY THE DOCUMENT'S OWN RESOLVER CLAMPS, WHICH `insuranceMoney`
 * DELIBERATELY DOES NOT. `agreement_merge_data` prints `LEAST(deposits, amount)` as what the
 * Borrower paid and `GREATEST(amount - deposits, 0)` as what was advanced (20260902010000:878,
 * 1157-1160) — its comment says a negative must never appear on a signed page. `insuranceMoney` is
 * unguarded on purpose, because its other reader is `get_contract_insights.total_value`, which is
 * genuinely unguarded; so the clamp is applied HERE, by handing it the capped figure, and the
 * shared arithmetic is left alone. On an overpaid year the two used to differ by the whole
 * overpayment, with a minus sign in front of it.
 */
export function printedMoney(
    premium: number | null,
    depositTotal: number,
    typedRate: number | null,
): PrintedMoney {
    const rate = depositsCoverPremium(premium, depositTotal) ? 0 : (typedRate ?? 0);
    const paid = premium === null ? depositTotal : Math.min(depositTotal, premium);
    return { ...insuranceMoney(premium, paid, rate), rate };
}
